// Shared enums / types, single source of truth for frontend + backend + workers.
// Backend owns labels and entitlements; frontend never hardcodes these maps.


/** Where a detected item lives (scan source). */
export const SourceType = {
  SEARCH: 'search', // Google, Bing, Yahoo, Yandex
  SOCIAL: 'social', // X, Instagram, Facebook, TikTok, Threads
  TUBE: 'tube', // video / tube sites
  FILEHOST: 'filehost', // cloud storage & file hosts
  IMAGEHOST: 'imagehost',
  FORUM: 'forum', // forums & blogs
  TORRENT: 'torrent',
  SHORTENER: 'shortener', // URL shorteners
  MESSENGER: 'messenger', // Telegram, Discord (higher tier)
  MANUAL: 'manual', // client-submitted URL
} as const;

export type SourceType = (typeof SourceType)[keyof typeof SourceType];

/**
 * Lifecycle of a domain case — one client, one domain, one round of
 * correspondence. Set by hand by support; the system only ever suggests a
 * next status (e.g. "every link answers 40x, close this?"), never flips it.
 */
export const DomainCaseStatus = {
  PENDING: 'pending', // nobody has picked it up yet
  HOLD: 'hold', // taken, deliberately parked (missing proof, legal check, ...)
  IN_PROGRESS: 'in_progress', // at least one channel has been sent
  DONE: 'done', // every link gone
  DECLINED: 'declined', // every usable channel refused, links still up
  CANCELLED: 'cancelled', // the complaint itself turned out invalid
} as const;

export type DomainCaseStatus = (typeof DomainCaseStatus)[keyof typeof DomainCaseStatus];

/** A case still open, one way or another. */
export const OPEN_CASE_STATUSES: readonly DomainCaseStatus[] = [
  DomainCaseStatus.PENDING,
  DomainCaseStatus.HOLD,
  DomainCaseStatus.IN_PROGRESS,
];

/** A case that will never move again. */
export const CLOSED_CASE_STATUSES: readonly DomainCaseStatus[] = [
  DomainCaseStatus.DONE,
  DomainCaseStatus.DECLINED,
  DomainCaseStatus.CANCELLED,
];

/**
 * Status of one channel's correspondence thread within a case. Sending is
 * always a human action — a thread never leaves `draft` on its own — so
 * every status past that one means a human clicked send.
 */
export const ChannelThreadStatus = {
  NOT_APPLICABLE: 'not_applicable', // no contact resolved for this channel
  DRAFT: 'draft', // system drafted a notice, nobody has sent it yet
  SENT: 'sent', // sent, no reply yet
  BOUNCED: 'bounced', // delivery itself failed
  ACKNOWLEDGED: 'acknowledged', // ticket-system auto-reply, not a person
  NEW_REPLY: 'new_reply', // a live reply landed, our turn
  NO_ANSWER: 'no_answer', // timed out with nothing back
  DECLINED: 'declined', // final refusal
  DONE: 'done', // final, content actually gone
} as const;

export type ChannelThreadStatus =
  (typeof ChannelThreadStatus)[keyof typeof ChannelThreadStatus];

/**
 * A thread that has ended without removing anything — what the DMCA gate
 * (see {@link TakedownChannel.DMCA}) and the "suggest closing" nudge both
 * read as "this channel is spent".
 */
export const CHANNEL_TERMINAL_NEGATIVE: readonly ChannelThreadStatus[] = [
  ChannelThreadStatus.DECLINED,
  ChannelThreadStatus.NO_ANSWER,
  // A channel support closed by hand — wrong contact, same company as one
  // that already answered, page gone — reads as `not_applicable` since
  // 2026-09-04, and that is spent for both readers too: the DMCA gate must
  // not wait forever on a channel nobody is working, and the nudge to close
  // the case must not pretend it is.
  ChannelThreadStatus.NOT_APPLICABLE,
];

/** A thread with a live back-and-forth still possible. */
export const CHANNEL_IN_FLIGHT: readonly ChannelThreadStatus[] = [
  ChannelThreadStatus.SENT,
  ChannelThreadStatus.ACKNOWLEDGED,
  ChannelThreadStatus.NEW_REPLY,
];

/**
 * A thread where the other side has actually said something — as opposed to
 * `sent`, which is a letter into silence.
 *
 * The distinction exists for the DMCA gate: three days of silence unlock the
 * paid channel, but a host who has answered (even with a ticket robot) is a
 * conversation in progress, and paying to go around it is what the gate is
 * there to prevent. `bounced` is not here on purpose: a delivery failure is
 * the absence of an answer, not one.
 */
export const CHANNEL_IN_FLIGHT_AFTER_REPLY: readonly ChannelThreadStatus[] = [
  ChannelThreadStatus.ACKNOWLEDGED,
  ChannelThreadStatus.NEW_REPLY,
];

/**
 * A thread sitting in a state that is our turn to answer — what
 * `DomainCaseSummary.needsAttention` is built from on the backend, and what
 * a case pane highlights on its own channel ladder. One list so the two
 * agree by construction instead of by two people remembering the same three
 * statuses.
 */
export const CHANNEL_NEEDS_ATTENTION: readonly ChannelThreadStatus[] = [
  ChannelThreadStatus.NEW_REPLY,
  ChannelThreadStatus.BOUNCED,
  ChannelThreadStatus.NO_ANSWER,
];

/** A thread that will never move again. */
export const CHANNEL_FINAL: readonly ChannelThreadStatus[] = [
  ChannelThreadStatus.DECLINED,
  ChannelThreadStatus.DONE,
];

/**
 * The channels a domain case can send to. Five are independent — support may
 * open any of them, in any order, whenever their contact resolves. `DMCA` is
 * the one gated channel: deliberately last-resort because dmca.com is paid.
 * See TakedownService.dmcaGateOpen for the actual gate.
 */
export const TakedownChannel = {
  DOMAIN_EMAIL: 'domain_email', // the site's own contact address
  HOSTER_ABUSE: 'hoster_abuse',
  REGISTRAR_ABUSE: 'registrar_abuse',
  CDN_ABUSE: 'cdn_abuse', // Cloudflare abuse API when RDAP is hidden
  SEARCH_DELISTING: 'search_delisting', // engine web forms
  DMCA: 'dmca', // dmca.com API, gated behind the other channels failing
  SOCIAL_TAKEDOWN: 'social_takedown', // fake profiles / leaked data on socials — not part of a domain case
} as const;

export type TakedownChannel = (typeof TakedownChannel)[keyof typeof TakedownChannel];

/** Display order in the channel ladder. `dmca` last on purpose. */
export const CHANNEL_ORDER: readonly TakedownChannel[] = [
  TakedownChannel.DOMAIN_EMAIL,
  TakedownChannel.HOSTER_ABUSE,
  TakedownChannel.REGISTRAR_ABUSE,
  TakedownChannel.CDN_ABUSE,
  TakedownChannel.SEARCH_DELISTING,
  TakedownChannel.DMCA,
];

/**
 * The four channels that resolve to a single stored address and need one
 * before they can send anything — `search_delisting` and `dmca` have no
 * contact concept at all (see `ProviderIntelService.contactFor`'s own doc).
 * Also exactly the set the Contacts tab's "add contact" form may target.
 */
export const DIRECT_CONTACT_CHANNELS: readonly TakedownChannel[] = [
  TakedownChannel.DOMAIN_EMAIL,
  TakedownChannel.HOSTER_ABUSE,
  TakedownChannel.REGISTRAR_ABUSE,
  TakedownChannel.CDN_ABUSE,
];

/**
 * Why support closed a case — the four outcomes `TakedownService.closeCase`
 * accepts, validated at the controller's `@IsIn` and offered by the close
 * modal from this one list, so neither side can drift from the other.
 */
/**
 * Whether a support member is at work.
 *
 * Not a permission and not a role: it says whether their clients are theirs to
 * work today. **Until it reads `ACTIVE` the panel does nothing for them** —
 * after signing in they switch themselves on, which is what stops a case being
 * worked by somebody whose clients the supervisor has already handed to a
 * stand-in.
 *
 * Only support has one. A supervisor back from leave who could not reassign
 * anybody would stop the entire queue, because reassigning is only theirs to
 * do. Dismissal is not here either — that is `User.disabledAt`, which refuses
 * the sign-in itself; "not available" and "no longer has access" are two
 * different facts and one column could not hold both.
 */
export enum SupportStatus {
  ACTIVE = 'active',
  DAY_OFF = 'dayoff',
  SICK = 'sick',
}

export const SUPPORT_STATUSES: readonly SupportStatus[] = [
  SupportStatus.ACTIVE,
  SupportStatus.DAY_OFF,
  SupportStatus.SICK,
];

/** Away, so their clients need somebody standing in. */
export const SUPPORT_AWAY: readonly SupportStatus[] = [SupportStatus.DAY_OFF, SupportStatus.SICK];

/**
 * What kind of site a domain is, in the words support works in.
 *
 * The queue is ordered by this, least trustworthy first: that is where the
 * leaks are, and where a notice is least likely to be argued with. It is a
 * judgement about the host, made once and reused on every future case there —
 * see `ProviderIntel.trustLevel`.
 *
 * There is no `whitelisted` member on purpose: a whitelisted domain never
 * reaches a queue to be sorted (see `GlobalWhitelist`), so having it here
 * would invite code that filters a list it can never appear in.
 */
export enum DomainTrustLevel {
  /** Adult tubes, the fapello-likes: what this product exists for. */
  PIRATE = 'pirate',
  /** Torrents, file hosts, and every domain nobody has judged yet. */
  SUSPICIOUS = 'suspicious',
  /** Code hosts, reddit, social platforms: look carefully before writing. */
  LEGITIMATE = 'legitimate',
}

/** Worst first — the order the queue is sorted in. */
export const DOMAIN_TRUST_LEVELS: readonly DomainTrustLevel[] = [
  DomainTrustLevel.PIRATE,
  DomainTrustLevel.SUSPICIOUS,
  DomainTrustLevel.LEGITIMATE,
];

/**
 * The queue's sort key, as stored on `DomainCase.triageRank`.
 *
 * A number rather than the word, because the database has to do the ordering:
 * the list is paged on a cursor, so sorting a page after fetching it would
 * order each page against itself. Derived from the level in one place, here,
 * so the two cannot disagree about which end of the queue is which.
 */
export function triageRankOf(level: string | null | undefined): number {
  const at = DOMAIN_TRUST_LEVELS.indexOf(level as DomainTrustLevel);
  // Unknown or unset reads as `suspicious` — an unjudged domain has to be
  // looked at, not parked behind everything already judged legitimate.
  return at >= 0 ? at : DOMAIN_TRUST_LEVELS.indexOf(DomainTrustLevel.SUSPICIOUS);
}

/**
 * Whether her own photos could confirm a finding — a tag for support, never a
 * gate on filing (decided 2026-09-02: every notice is sent by a person, so
 * nothing here blocks anything).
 *
 * The three states exist because two of them used to be one. "We had no photo
 * to compare against" was recorded identically to "we compared and it is not
 * her", which is what refused 80% of real findings.
 */
export enum PhotoMatchState {
  /** She has uploaded nothing to compare against, or the check never ran. */
  NO_PHOTO = 'no_photo',
  /** Compared against her references, and it is her. */
  MATCHES = 'matches',
  /** Compared, and it is not — worth a careful look before writing. */
  NO_MATCH = 'no_match',
}

export enum CaseCloseReason {
  ALL_REMOVED = 'all_removed',
  /** Every link went before any notice left. Set by the system only, so it is
   *  not one of the reasons a person picks below; the client reads it as
   *  Removed like any other, the team can still tell it apart (DEC-004). */
  GONE_BEFORE_FILING = 'gone_before_filing',
  ALL_DECLINED = 'all_declined',
  INVALID = 'invalid',
  OTHER = 'other',
}

export const CASE_CLOSE_REASONS: readonly CaseCloseReason[] = [
  CaseCloseReason.ALL_REMOVED,
  CaseCloseReason.ALL_DECLINED,
  CaseCloseReason.INVALID,
  CaseCloseReason.OTHER,
];

/**
 * What an account pays for; no subscription is null, not a code. The last three
 * are not sold and stay because billing events name them (see LEGACY_PLAN).
 */
export const PlanCode = {
  ESSENTIAL: 'essential',
  PRO: 'pro',
  PREMIUM: 'premium',
  AGENCY_STARTER: 'agency_starter',
  AGENCY_GROWTH: 'agency_growth',
  AGENCY_SCALE: 'agency_scale',
  PROTECTION: 'protection',
  SIXTYNINE: 'sixtynine',
  AFTERGLOW: 'afterglow',
} as const;

export type PlanCode = (typeof PlanCode)[keyof typeof PlanCode];


/**
 * What a person sees instead of the code: the panel's plan dropdown, a
 * client's own page, a spend report, wherever a plan is named for a human
 * rather than matched against in code. One table, so a rename is one edit
 * instead of a grep across both apps to find every place that spelled it out
 * by hand and hoping none were missed.
 */
export const PLAN_LABEL: Record<PlanCode, string> = {
  [PlanCode.ESSENTIAL]: 'Essential',
  [PlanCode.PRO]: 'Pro',
  [PlanCode.PREMIUM]: 'Premium',
  [PlanCode.AGENCY_STARTER]: 'Starter',
  [PlanCode.AGENCY_GROWTH]: 'Growth',
  [PlanCode.AGENCY_SCALE]: 'Scale',
  [PlanCode.PROTECTION]: 'Pro (was Protection)',
  [PlanCode.SIXTYNINE]: 'Pro (was SixtyNine)',
  [PlanCode.AFTERGLOW]: 'Premium (was Afterglow)',
};

/**
 * The plan a retired code is served as. Their subscriptions keep the price
 * they were bought at, and a webhook still carries the old code in its
 * metadata, which is why the code is mapped on every read rather than once.
 */
export const LEGACY_PLAN: Partial<Record<PlanCode, PlanCode>> = {
  [PlanCode.PROTECTION]: PlanCode.PRO,
  [PlanCode.SIXTYNINE]: PlanCode.PRO,
  [PlanCode.AFTERGLOW]: PlanCode.PREMIUM,
};

/** The plan a code is served as; null for no subscription, whatever it was stored as. */
export function currentPlan(code: PlanCode | string | null | undefined): PlanCode | null {
  if (!code) return null;
  return LEGACY_PLAN[code as PlanCode] ?? (code as PlanCode);
}

/** The admin's word for taking a subscription away, since null is not a choice in a list. */
export const NO_PLAN_CHOICE = 'none';

/** What a person reads for no subscription, wherever a plan would be named. */
export const NO_PLAN_LABEL = 'No subscription';

export function planLabel(code: PlanCode | string | null | undefined): string {
  const plan = currentPlan(code);
  return plan ? PLAN_LABEL[plan] ?? String(code) : NO_PLAN_LABEL;
}

/** What is sold, in the order a price list shows it. */
export const CREATOR_PLANS = [PlanCode.ESSENTIAL, PlanCode.PRO, PlanCode.PREMIUM] as const;
export const AGENCY_PLANS = [PlanCode.AGENCY_STARTER, PlanCode.AGENCY_GROWTH, PlanCode.AGENCY_SCALE] as const;

/** Sites named on the live screen before a subscription; the rest are locked. */
export const FREE_VISIBLE_SITES = 3;

/** Findings named on the findings list before a subscription; the rest are
 *  locked. Enforced server-side (`FindingsService.listFindings`), not only by
 *  which rows the page chooses to draw. */
export const FREE_VISIBLE_FINDINGS = 5;

export function isAgencyPlan(code: PlanCode | null): boolean {
  const plan = currentPlan(code);
  return plan !== null && (AGENCY_PLANS as readonly PlanCode[]).includes(plan);
}

/**
 * List prices, in dollars a month; a manager's price setting wins over these.
 * `perProfile` is the agency's per-creator charge on top of the base (Scale's
 * is quoted, so only its floor is here). `annual` is the year's total at a 20%
 * discount (month × 12 × 0.8), overridden by `PRICE_*_ANNUAL_USD`.
 */
export const PLAN_LIST_PRICE: Partial<Record<PlanCode, { base: number; perProfile?: number; annual?: number }>> = {
  [PlanCode.ESSENTIAL]: { base: 89, annual: 854 },
  [PlanCode.PRO]: { base: 149, annual: 1430 },
  [PlanCode.PREMIUM]: { base: 299, annual: 2870 },
  [PlanCode.AGENCY_STARTER]: { base: 499, perProfile: 49 },
  [PlanCode.AGENCY_GROWTH]: { base: 999, perProfile: 39 },
  [PlanCode.AGENCY_SCALE]: { base: 1500, perProfile: 20 },
};

/**
 * Which shelf of the atlas a domain sits on: what the product is for
 * (`leaks`), where the client herself posts (`platform`), hers or harmless
 * (`whitelist`), in the trade but not a leak site (`no_category`), and found
 * but not yet judged (`in_review`).
 */
export enum SiteCategory {
  leaks = 'leaks',
  no_category = 'no_category',
  whitelist = 'whitelist',
  platform = 'platform',
  in_review = 'in_review',
}

/** Whether a domain can be searched by URL, and what the last attempt found. */
export enum SiteSearchState {
  works = 'works',
  manual = 'manual',
  dead = 'dead',
  unknown = 'unknown',
}

export enum ScanFrequency {
  /** Exactly one scan, ever — the one that runs the moment a profile is ready. */
  ONCE = 'once',
  WEEKLY = 'weekly',
  DAILY = 'daily',
}

export interface PlanEntitlements {
  scanFrequency: ScanFrequency;
  /**
   * The cadence word shown on the public plan card. Separate from scanFrequency,
   * which drives the engine (only daily/weekly exist there): a plan may be sold
   * as "Hourly" while the sweep runs daily. One source of truth, set per plan.
   */
  cardScanFrequency: string;
  usernamesIncluded: number;
  /**
   * Profiles the plan protects. An agency's number is what it bought, so its
   * plan says the least it may buy (`minProfiles`) and this is 0.
   */
  profilesIncluded: number;
  minProfiles: number;
  /** Hard backend guard: false = findings are read-only, no cases are built. */
  canTakedown: boolean;
  /**
   * Complexity points a month. Every removal filed spends points by how hard
   * it is (POINT_WEIGHT); Infinity is a plan that is not metered.
   */
  pointsPerMonth: number;
  whitelisting: boolean;
  sources: SourceType[];
  /** How often a summary is sent: none, monthly, or weekly. */
  reports: 'none' | 'monthly' | 'weekly';
  /** Place in the support queue: 0 normal, 1 priority, 2 priority with an SLA. */
  priority: 0 | 1 | 2;
  /** One agency member per creator, and roles beyond owner, manager, viewer. */
  assignments: boolean;
  whiteLabel: boolean;
  apiAccess: boolean;
  /**
   * Which indexes a plan's sweeps are asked of. A second engine asks the same
   * question where Google has not indexed: Bing keeps pages Google has already
   * delisted, so the one still findable is exactly the one she paid to remove.
   * It also doubles the searches — $1.99/day Pro and $2.43/day Pro+ against a
   * $1.25 ceiling — so neither is entitled to it until it can be afforded.
   */
  engines: SearchEngine[];
}

/**
 * A search index we can buy results from.
 *
 * Two, because two is what the provider serves for the SERP endpoints this
 * product uses. Named rather than left as strings so a plan cannot ask for an
 * engine that does not exist and find out at the first sweep.
 */
export const SearchEngine = {
  GOOGLE: 'google',
  BING: 'bing',
} as const;

export type SearchEngine = (typeof SearchEngine)[keyof typeof SearchEngine];

/**
 * Server-side entitlement table. Never duplicate this on the frontend.
 *
 * `sources` went unread for as long as it existed, and the day the sweep began
 * honouring it it would have switched off the tube list on every plan — the one
 * category this product is for, carrying 47.7% of the removals filed for our
 * clients' population. The lists below are what each tier really searches, and
 * the reasoning is cost: the tube list is fourteen queries per term, each with a
 * `site:` chain at five times the base price. The free tier gets the two places
 * most likely to hold a first finding, because a free scan that finds nothing
 * lies about what we do.
 */
const ALL_SOURCES = [
  SourceType.SEARCH,
  SourceType.SOCIAL,
  SourceType.TUBE,
  SourceType.TORRENT,
  SourceType.FILEHOST,
  SourceType.MESSENGER,
  SourceType.FORUM,
];

const PAID = { canTakedown: true, whitelisting: true, engines: [SearchEngine.GOOGLE] };
const NOT_AGENCY = { minProfiles: 0, assignments: false, whiteLabel: false, apiAccess: false };

const ESSENTIAL: PlanEntitlements = {
  ...PAID, ...NOT_AGENCY,
  scanFrequency: ScanFrequency.WEEKLY,
  cardScanFrequency: 'Weekly',
  usernamesIncluded: 5,
  profilesIncluded: 1,
  pointsPerMonth: 50,
  // No social platforms and no messengers: the two the plan leaves out.
  sources: [SourceType.SEARCH, SourceType.TUBE, SourceType.TORRENT, SourceType.FILEHOST, SourceType.FORUM],
  reports: 'none',
  priority: 0,
};
const PRO: PlanEntitlements = {
  ...ESSENTIAL,
  scanFrequency: ScanFrequency.DAILY,
  cardScanFrequency: 'Daily',
  pointsPerMonth: 150,
  sources: ALL_SOURCES.filter((one) => one !== SourceType.MESSENGER),
  reports: 'monthly',
  priority: 1,
};
/* Sold as the more aggressive cadence and searched daily like Pro: the
   difference it pays for is the sources, the profiles and the queue. */
const PREMIUM: PlanEntitlements = {
  ...PRO,
  // Sold as hourly on the card; the sweep still runs daily like Pro (see above).
  cardScanFrequency: 'Hourly',
  usernamesIncluded: 15,
  profilesIncluded: 3,
  pointsPerMonth: 400,
  sources: ALL_SOURCES,
  reports: 'weekly',
  priority: 2,
};
const AGENCY: PlanEntitlements = {
  ...PREMIUM,
  usernamesIncluded: 0,
  profilesIncluded: 0,
  reports: 'monthly',
  priority: 1,
  assignments: false,
  whiteLabel: false,
  apiAccess: false,
};

/**
 * No subscription: the one search that shows her what is out there, found and
 * not acted on. Not a plan, so it is not in the table below.
 */
export const NO_PLAN: PlanEntitlements = {
  ...NOT_AGENCY,
  scanFrequency: ScanFrequency.ONCE,
  cardScanFrequency: 'One scan',
  usernamesIncluded: 1,
  profilesIncluded: 1,
  canTakedown: false, // shown but not actionable: upsell hook
  pointsPerMonth: 0,
  whitelisting: false,
  // An honest first result for little: open web, plus the sites this trade runs on.
  sources: [SourceType.SEARCH, SourceType.TUBE],
  engines: [SearchEngine.GOOGLE],
  reports: 'none',
  priority: 0,
};

export const PLAN_ENTITLEMENTS: Record<PlanCode, PlanEntitlements> = {
  [PlanCode.ESSENTIAL]: ESSENTIAL,
  [PlanCode.PRO]: PRO,
  [PlanCode.PREMIUM]: PREMIUM,
  [PlanCode.AGENCY_STARTER]: { ...AGENCY, minProfiles: 10, pointsPerMonth: 800 },
  [PlanCode.AGENCY_GROWTH]: { ...AGENCY, minProfiles: 30, pointsPerMonth: 2500, assignments: true, whiteLabel: true, priority: 2 },
  [PlanCode.AGENCY_SCALE]: {
    ...AGENCY, minProfiles: 100, pointsPerMonth: Number.POSITIVE_INFINITY,
    assignments: true, whiteLabel: true, apiAccess: true, priority: 2,
  },
  [PlanCode.PROTECTION]: PRO,
  [PlanCode.SIXTYNINE]: PRO,
  [PlanCode.AFTERGLOW]: PREMIUM,
};

/** What an account may do: its plan's, or the one search without one. */
export function entitlementsFor(code: PlanCode | string | null | undefined): PlanEntitlements {
  const plan = currentPlan(code);
  return (plan && PLAN_ENTITLEMENTS[plan]) || NO_PLAN;
}

/**
 * Points one filed removal costs, by where the page is. A leak site answers a
 * notice; a social platform wants its own form per account, and a Telegram
 * channel is reported and chased. Changed from admin settings (`points.*`).
 */
export const POINT_WEIGHT: Record<SourceType, number> = Object.fromEntries(
  Object.values(SourceType).map((one) => [one, one === SourceType.MESSENGER ? 3 : one === SourceType.SOCIAL || one === SourceType.FORUM ? 2 : 1]),
) as Record<SourceType, number>;

/**
 * The three role vocabularies the database also holds, declared here once.
 *
 * `schema.prisma` has them as `enum` blocks because columns need them; those
 * blocks are checked against these by scripts/enums-match-schema.mjs, so a
 * member added on either side fails the build instead of disagreeing quietly.
 * They cannot be imported from the client instead — web and admin would pull
 * Prisma into a browser bundle — which is why the declaration lives on this
 * side of that line and the schema is what gets verified.
 *
 * Written as a frozen map plus its union rather than a TypeScript `enum` so
 * that `role === 'ADMIN'` still type-checks: these arrive as strings from a
 * JWT, a row and a query parameter, and every guard already compares them
 * that way.
 */
export const UserRole = {
  CLIENT: 'CLIENT',
  SUPPORT: 'SUPPORT',
  SUPERVISOR: 'SUPERVISOR',
  ADMIN: 'ADMIN',
  DEVELOPER: 'DEVELOPER',
  FINANCIER: 'FINANCIER',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/**
 * Which door a user came in by: one email can hold a customer account and a
 * staff account, told apart by this rather than by role (a CLIENT is a customer,
 * everyone else staff, but the kind is what the two sign-in doors set).
 */
export const UserKind = {
  CUSTOMER: 'CUSTOMER',
  STAFF: 'STAFF',
} as const;
export type UserKind = (typeof UserKind)[keyof typeof UserKind];

export const AccountKind = {
  SOLO: 'SOLO',
  AGENCY: 'AGENCY',
} as const;
export type AccountKind = (typeof AccountKind)[keyof typeof AccountKind];

/** The number a person says out loud, "CL-2041" or "AG-118": see Account.accountNo. */
export function accountRef(kind: string, accountNo: number): string {
  return `${kind === AccountKind.AGENCY ? 'AG' : 'CL'}-${accountNo}`;
}

export const AccountRole = {
  OWNER: 'OWNER',
  MANAGER: 'MANAGER',
  VIEWER: 'VIEWER',
} as const;
export type AccountRole = (typeof AccountRole)[keyof typeof AccountRole];

/**
 * A named subset of one of those vocabularies.
 *
 * The sets below are permission lists, and they used to be bare `as const`
 * arrays of strings: a typo compiled, and a role renamed in the schema left a
 * set quietly granting nothing. Passing the members through here constrains
 * them to real roles while keeping each set's own literal type, which is what
 * `AssignableAccountRole` and the label maps are derived from.
 */
const userRoles = <T extends readonly UserRole[]>(...roles: T): T => roles;
const accountRoles = <T extends readonly AccountRole[]>(...roles: T): T => roles;

/**
 * Who signs in through the staff door — nothing more. Which endpoints they may
 * reach is `OPERATIONAL_ROLES`, `ADMIN_ROLES` and `FINANCE_ROLES` below.
 */
export const STAFF_ROLES = userRoles('SUPPORT', 'SUPERVISOR', 'ADMIN', 'DEVELOPER', 'FINANCIER');

/**
 * Does this role sign in through the staff door?
 *
 * The cast the check needs is the reason this exists: three files had written
 * `(STAFF_ROLES as readonly string[]).includes(role)` for themselves, which is
 * three places to miss when the list grows a fifth member, and the cast is
 * exactly the sort of line somebody simplifies without noticing what it was
 * widening.
 */
export function isStaffRole(role: string): boolean {
  return (STAFF_ROLES as readonly string[]).includes(role);
}

/**
 * Case review, the support inbox, hosts, scans, reference assets, sign-ups,
 * team invites, usage quotas: the operational half of the panel, as opposed
 * to money (`FINANCE_ROLES`) or configuration (`ADMIN_ROLES`). This is what
 * `STAFF_ROLES` meant everywhere in this codebase before FINANCIER existed;
 * every one of those call sites was a place a finance-only account had no
 * business reaching, so they were moved onto this instead of onto a
 * `STAFF_ROLES` that had quietly grown a fourth member.
 */
export const OPERATIONAL_ROLES = userRoles('SUPPORT', 'SUPERVISOR', 'ADMIN', 'DEVELOPER');

/**
 * Everything an admin may open. DEVELOPER is an admin who may also read a key
 * back out of the database, so it belongs in every place ADMIN does, writing
 * `'ADMIN'` by hand in a guard is how one endpoint quietly stops working for
 * the person who most often needs it.
 */
export const ADMIN_ROLES = userRoles('ADMIN', 'DEVELOPER');

/**
 * Who may hand a client to somebody, cover an absence, or move work between
 * people — and who therefore sees every client rather than their own.
 *
 * The supervisor's own list, not `ADMIN_ROLES` with a new member: an admin
 * configures the product and happens to be allowed to run a shift too, while a
 * supervisor runs shifts and has no business in the mail server's password.
 * Written as its own set so the day somebody asks "who can reassign a client"
 * the answer is a list rather than a search.
 */
export const SUPERVISOR_ROLES = userRoles('SUPERVISOR', 'ADMIN', 'DEVELOPER');

/** Does this role see every client, rather than the ones assigned to them? */
export function seesEveryClient(role: string): boolean {
  return (SUPERVISOR_ROLES as readonly string[]).includes(role);
}

/**
 * Payments and search spend: real money, and what it's costing. FINANCIER
 * is let in beside the two admin roles; the operational roles (support
 * included) are not, because "what did this cost" and "is this notice
 * filed" are different questions asked by different people.
 */
export const FINANCE_ROLES = userRoles('ADMIN', 'DEVELOPER', 'FINANCIER');

/**
 * What the Team page may hand out.
 *
 * DEVELOPER is deliberately absent: it can read live credentials, so it is not
 * a checkbox on a form that adds a colleague. It is granted by a migration or
 * by a row written on purpose, which is a decision with a date on it rather
 * than a click. The page still shows and can revoke whoever holds it.
 */
export const ASSIGNABLE_STAFF_ROLES = userRoles('SUPPORT', 'SUPERVISOR', 'ADMIN', 'FINANCIER');

/**
 * Declared here because it was declared twice -- the same
 * `(typeof STAFF_ROLES)[number]` in `staff.service.ts` and again in the admin
 * Team page, so a role added to the list reached one of them.
 */
export type StaffRole = (typeof STAFF_ROLES)[number];

export type AssignableStaffRole = (typeof ASSIGNABLE_STAFF_ROLES)[number];

/**
 * What an account may hand out to its own people. OWNER is absent: there is
 * exactly one per account and ownership transfers rather than being shared, so
 * an invite form cannot grant it.
 *
 * Here rather than in the controller, which held the list twice as an `@IsIn`
 * and twice again as a union — four copies in one file, and adding a role would
 * have updated some of them.
 */
export const ASSIGNABLE_ACCOUNT_ROLES = accountRoles('MANAGER', 'VIEWER');

export type AssignableAccountRole = (typeof ASSIGNABLE_ACCOUNT_ROLES)[number];

/**
 * What each account role is called and may do, in the words a client reads
 * while choosing. Beside the list, because the invite form had its own copy: a
 * role added here would have reached the API and not the form that grants it,
 * and a description edited there would have disagreed with nothing.
 *
 * OWNER has a label and no description — it is shown against somebody who
 * already holds it and is never offered.
 */
export const ACCOUNT_ROLE_LABEL: Record<AccountRole, string> = {
  OWNER: 'Owner',
  MANAGER: 'Manager',
  VIEWER: 'Viewer',
};

export const ACCOUNT_ROLE_HINT: Record<AssignableAccountRole, string> = {
  MANAGER: 'Add models, run searches, ask for removals. Not billing, not people.',
  VIEWER: 'Reads everything, changes nothing. For an assistant or an accountant.',
};

/**
 * Audience size, picked from a band rather than typed as a number.
 *
 * Nobody knows their follower count across four platforms to the digit, and a
 * free-text box invited "about 2 million" and "2.4M" into the same column. The
 * bands are the answer we can actually use, and the en dashes are part of the
 * stored value — the string is what the summary prints.
 */
export const FOLLOWER_RANGES = [
  'Under 1K',
  '1K–5K',
  '5K–10K',
  '10K–50K',
  '50K–100K',
  '100K–500K',
  '500K–1M',
  '1M–5M',
  '5M+',
] as const;

export type FollowerRange = (typeof FOLLOWER_RANGES)[number];

/**
 * What the liveness check last found at a URL. `RETURNED` is not one of the
 * probe's four: a search result is not a fetch, so a known finding seen in an
 * index again asks for a re-check without claiming we looked. The probe
 * overwrites it.
 */
export const Liveness = {
  LIVE: 'live',
  GONE: 'gone',
  BLOCKED: 'blocked',
  UNKNOWN: 'unknown',
  RETURNED: 'returned',
} as const;

export type Liveness = (typeof Liveness)[keyof typeof Liveness];

/**
 * Whether we managed to look at all, which is not the same question as what
 * we found: a page that refused the check is not a page without her content
 * in it.
 */
export const CheckState = {
  CHECKED: 'checked',
  FETCH_BLOCKED: 'fetch_blocked',
  NOT_CHECKED: 'not_checked',
} as const;

export type CheckState = (typeof CheckState)[keyof typeof CheckState];

/**
 * The four states a signed authorization letter can be in. `pending` is the
 * column's default and means nothing has arrived; `verified` is a human
 * decision and the only state the filing guard accepts.
 */
export const AuthorizationStatus = {
  PENDING: 'pending',
  UPLOADED: 'uploaded',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
} as const;

export type AuthorizationStatus =
  (typeof AuthorizationStatus)[keyof typeof AuthorizationStatus];

/** How a scan run ended. A row still `running` is a scan that died. */
export const ScanRunStatus = {
  RUNNING: 'running',
  COMPLETE: 'complete',
  FAILED: 'failed',
} as const;

export type ScanRunStatus = (typeof ScanRunStatus)[keyof typeof ScanRunStatus];

/** What one paid WHOIS lookup came to: an address, an answer without one, a TLD the service does not cover, or no answer. */
export const WhoisOutcome = {
  FOUND: 'found',
  NO_ADDRESS: 'no_address',
  NOT_COVERED: 'not_covered',
  FAILED: 'failed',
} as const;

export type WhoisOutcome = (typeof WhoisOutcome)[keyof typeof WhoisOutcome];

/** What DNS said about a catalogue domain before the prefill spent a WHOIS on it (DEC-035). */
export const DnsState = {
  ALIVE: 'alive',
  NXDOMAIN: 'nxdomain',
  NO_ADDRESS: 'no_address',
  UNRESOLVED: 'unresolved',
} as const;

export type DnsState = (typeof DnsState)[keyof typeof DnsState];

/** Who answered a lookup, as a person names them: the paid WHOIS, or the registry's free RDAP (DEC-037). */
export const WHOIS_SERVICE_NAME: Readonly<Record<string, string>> = {
  apilayer: 'APILayer (paid)',
  rdap: 'RDAP (free)',
  // A sales or support box replaced by the complaints desk the same registrar gave us elsewhere (DEC-039).
  known_desk: 'Known registrar desk',
};

/** Why a paid WHOIS request was made: the catalogue prefill, a case waiting on an address, or a person opening the domain. */
export const WhoisReason = {
  PREFILL: 'prefill',
  CASE: 'case',
  MANUAL: 'manual',
} as const;

export type WhoisReason = (typeof WhoisReason)[keyof typeof WhoisReason];

/** Where a host's abuse address came from: the paid WHOIS or support's own hand (DEC-034). */
export const ContactSource = {
  PROVIDER: 'provider',
  MANUAL: 'manual',
} as const;

export type ContactSource = (typeof ContactSource)[keyof typeof ContactSource];

/** Where a client is talking to support. */
export const SupportChannel = {
  WHATSAPP: 'whatsapp',
  WEB: 'web',
} as const;

export type SupportChannel = (typeof SupportChannel)[keyof typeof SupportChannel];

export const SupportThreadStatus = {
  OPEN: 'open',
  CLOSED: 'closed',
} as const;

export type SupportThreadStatus =
  (typeof SupportThreadStatus)[keyof typeof SupportThreadStatus];

/** A Help ticket's state. Labels the client reads are in `TICKET_STATUS`. */
export const SupportTicketStatus = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  WAITING_CLIENT: 'waiting_client',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

export type SupportTicketStatus = (typeof SupportTicketStatus)[keyof typeof SupportTicketStatus];

/** "What do you need help with?" on the ticket form. */
export const SupportRequestType = {
  ACCOUNT: 'account',
  SCANS: 'scans',
  CASE_ISSUE: 'case_issue',
  MANUAL_REPORT: 'manual_report',
  AUTHORIZATION: 'authorization',
  BILLING: 'billing',
  TECHNICAL: 'technical',
  FEATURE_REQUEST: 'feature_request',
  OTHER: 'other',
} as const;

export type SupportRequestType = (typeof SupportRequestType)[keyof typeof SupportRequestType];

/**
 * Stripe's own subscription statuses, plus `none` for an account it has never
 * been asked about. A status Stripe adds is not in here, so the write checks
 * first and leaves the column alone rather than failing the webhook.
 */
export const BillingStatus = {
  /** Stripe has never been asked about this account: it has no subscription. */
  NONE: 'none',
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  UNPAID: 'unpaid',
  INCOMPLETE: 'incomplete',
  INCOMPLETE_EXPIRED: 'incomplete_expired',
  PAUSED: 'paused',
} as const;

export type BillingStatus = (typeof BillingStatus)[keyof typeof BillingStatus];

/** How often a subscription renews. Creator plans can be billed monthly or
 *  yearly (yearly at a 20% discount); agency plans are monthly only. */
export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export function isBillingInterval(value: unknown): value is BillingInterval {
  return value === 'month' || value === 'year';
}

/** Paying, as far as Stripe is concerned. */
export function billingIsActive(status: string): boolean {
  return status === BillingStatus.ACTIVE;
}

/**
 * A word from outside — a URL segment, a mail header, Stripe's metadata — as a
 * member of a vocabulary, or null. The columns are enums now, so an unchecked
 * word is a failed write rather than a bad row; this is where it becomes a
 * refusal the caller can answer with.
 */
export function memberOf<T extends string>(
  vocabulary: Record<string, T>,
  word: string | null | undefined,
): T | null {
  const values: readonly string[] = Object.values(vocabulary);
  return typeof word === 'string' && values.includes(word) ? (word as T) : null;
}

/** Which way one letter in a thread went. */
export const MessageDirection = {
  IN: 'in',
  OUT: 'out',
} as const;

export type MessageDirection = (typeof MessageDirection)[keyof typeof MessageDirection];

/**
 * The things this product owes a client a word about. Three places read them:
 * the sweep that writes a row, the list the app renders, and the preference
 * that decides whether an email leaves.
 */
export const NotificationKind = {
  FINDING: 'finding',
  WEEKLY_DIGEST: 'weekly_digest',
  CASE_RESOLVED: 'case_resolved',
  AUTHORIZATION: 'authorization',
  QUOTA: 'quota',
  /** What is missing from her own setup, and what it costs her. */
  SETUP: 'setup',
  SCAN_COMPLETED: 'scan_completed',
  SCAN_FAILED: 'scan_failed',
  /** Removals we have opened on her behalf. */
  CASE_OPENED: 'case_opened',
  /** A notice actually left for a host: the step between opened and resolved. */
  CASE_UPDATE: 'case_update',
  /** Support answered one of her tickets. */
  SUPPORT_REPLY: 'support_reply',
  /** A ticket was filed, or support moved its status. */
  SUPPORT_UPDATE: 'support_update',
} as const;

export type NotificationKind = (typeof NotificationKind)[keyof typeof NotificationKind];

/** What support is told about, addressed to the person whose case it is. */
export const StaffNotificationKind = {
  CASE_REPLY: 'case_reply',
  CASE_LATE_REPLY: 'case_late_reply',
  CASE_BOUNCED: 'case_bounced',
  CASE_SILENT: 'case_silent',
  CONTENT_RETURNED: 'content_returned',
} as const;

export type StaffNotificationKind =
  (typeof StaffNotificationKind)[keyof typeof StaffNotificationKind];

/**
 * Which part raised an error. The last three are the only ones a browser can
 * raise, which is what the panel's Frontend/Backend split is drawn on.
 */
export const ErrorSource = {
  API: 'api',
  CRON: 'cron',
  JOBS: 'jobs',
  RENDER: 'render',
  ERROR: 'error',
  UNHANDLEDREJECTION: 'unhandledrejection',
} as const;

export type ErrorSource = (typeof ErrorSource)[keyof typeof ErrorSource];

/**
 * How a query was asked: across the open web, or once per targeted site. The
 * pair is what makes an Open-vs-Targeted comparison possible.
 */
export const SearchChannel = {
  OPEN: 'open',
  SITE: 'site',
} as const;

export type SearchChannel = (typeof SearchChannel)[keyof typeof SearchChannel];

/**
 * The members of a vocabulary among these words, and the words that are not
 * members. A filter narrowed to nothing recognisable is a caller's mistake,
 * not an empty result, so the rejects come back rather than being dropped.
 */
export function membersOf<T extends string>(
  vocabulary: Record<string, T>,
  words: readonly string[] | undefined,
): { members: T[]; rejected: string[] } {
  const members: T[] = [];
  const rejected: string[] = [];
  for (const word of words ?? []) {
    const member = memberOf(vocabulary, word);
    if (member) members.push(member);
    else rejected.push(word);
  }
  return { members, rejected };
}

/**
 * What the findings list narrows to. `unfiled` and `filed` are not case
 * statuses -- they are the presence or absence of a case, which is the state
 * most people actually look for -- so they sit beside the six.
 */
export const FINDING_FILING = {
  UNFILED: 'unfiled',
  FILED: 'filed',
} as const;

export type FindingFilter =
  | (typeof FINDING_FILING)[keyof typeof FINDING_FILING]
  | DomainCaseStatus;

/**
 * Every verb the case history can record — audit-log verbs, not case states.
 * `contact_added` and `whois_refreshed` are labelled and drawn and nothing
 * writes them; `merged` is historical, and rows hold it.
 */
export const CaseEventType = {
  OPENED: 'opened',
  URL_ADDED: 'url_added',
  TAKEN: 'taken',
  ASSIGNED: 'assigned',
  HELD: 'held',
  UNHELD: 'unheld',
  SENT: 'sent',
  SEND_FAILED: 'send_failed',
  REPLY: 'reply',
  BOUNCED: 'bounced',
  ACKNOWLEDGED: 'acknowledged',
  LATE_REPLY: 'late_reply',
  COUNTER_NOTICE: 'counter_notice',
  DECLINED: 'declined',
  NO_ANSWER: 'no_answer',
  CHANNEL_DONE: 'channel_done',
  CHANNEL_CLOSED: 'channel_closed',
  ALL_REMOVED: 'all_removed',
  EVIDENCE_CAPTURED: 'evidence_captured',
  EVIDENCE_CAPTURE_FAILED: 'evidence_capture_failed',
  CONTACT_ADDED: 'contact_added',
  WHOIS_REFRESHED: 'whois_refreshed',
  COLLECTION_STOPPED: 'collection_stopped',
  SPLIT: 'split',
  NEW_ROUND: 'new_round',
  MERGED: 'merged',
  RETURNED: 'returned',
  REOPENED: 'reopened',
  CLOSED: 'closed',
  HANDED_OVER: 'handed_over',
  COVERED: 'covered',
  HANDED_BACK: 'handed_back',
} as const;

export type CaseEventType = (typeof CaseEventType)[keyof typeof CaseEventType];

/** An invitation's own life. Derived from three timestamps, not a column. */
export const InviteStatus = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REVOKED: 'revoked',
  EXPIRED: 'expired',
} as const;

export type InviteStatus = (typeof InviteStatus)[keyof typeof InviteStatus];

/** What one Stripe webhook meant to the account, in our words not Stripe's. */
export const BillingEventOutcome = {
  CHECKOUT_COMPLETED: 'checkout_completed',
  EXTRA_USERNAME_PURCHASED: 'extra_username_purchased',
  RENEWAL_PAID: 'renewal_paid',
  INVOICE_PAID: 'invoice_paid',
  PAYMENT_FAILED: 'payment_failed',
  DISPUTED: 'disputed',
  REFUNDED: 'refunded',
  UNHANDLED: 'unhandled',
  UNMATCHED: 'unmatched',
} as const;

export type BillingEventOutcome =
  (typeof BillingEventOutcome)[keyof typeof BillingEventOutcome];

/** A Stripe invoice's own status, in the words printed on the document. */
export const InvoiceStatus = {
  DRAFT: 'draft',
  OPEN: 'open',
  PAID: 'paid',
  UNCOLLECTIBLE: 'uncollectible',
  VOID: 'void',
} as const;

export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

/** Why a scan ran. */
export const ScanMode = {
  SCHEDULED: 'scheduled',
  LIVE: 'live',
  FIRST: 'first',
} as const;

export type ScanMode = (typeof ScanMode)[keyof typeof ScanMode];

/**
 * Confidence in bands, because nobody filters for "between 0.82 and 0.87".
 * The wire spelling: this is what the findings filter sends.
 */
export const MatchConfidence = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  UNCHECKED: 'unchecked',
} as const;

export type MatchConfidence = (typeof MatchConfidence)[keyof typeof MatchConfidence];

/** A metered allowance. One today; named rather than spelled at three sites. */
export const QuotaMetric = {
  ON_DEMAND_TAKEDOWNS: 'on_demand_takedowns',
} as const;

export type QuotaMetric = (typeof QuotaMetric)[keyof typeof QuotaMetric];

/** A domain case's six states, as the five a sender of a link reads. */
export const SubmissionStatus = {
  UNDER_REVIEW: 'under_review',
  CASE_CREATED: 'case_created',
  REMOVAL_IN_PROGRESS: 'removal_in_progress',
  REMOVED: 'removed',
  REJECTED: 'rejected',
} as const;

export type SubmissionStatus = (typeof SubmissionStatus)[keyof typeof SubmissionStatus];

/**
 * A finding's own status, read only from the finding's record. A case on the
 * same link has its own status; reading this off the case would make support
 * closing a whole domain silently rewrite every link in it.
 */
export const RowStatus = {
  FOUND: 'found',
  DELISTED: 'delisted',
  REMOVED: 'removed',
  FALSE_POSITIVE: 'false_positive',
  WHITELISTED: 'whitelisted',
} as const;

export type RowStatus = (typeof RowStatus)[keyof typeof RowStatus];

/**
 * How a batch of queries is bought. Same SERP, very different prices: `live`
 * is for a button somebody is watching, `task` is everything else.
 */
export const SearchMode = {
  LIVE: 'live',
  TASK: 'task',
} as const;

export type SearchMode = (typeof SearchMode)[keyof typeof SearchMode];

/** Why a scan searched as deep as it did, for the spend log. */
export const DepthReason = {
  FIRST_SCAN: 'first_scan',
  RENEWAL_WINDOW: 'renewal_window',
  STEADY_STATE: 'steady_state',
} as const;

export type DepthReason = (typeof DepthReason)[keyof typeof DepthReason];

/**
 * A priced search that belongs to no profile's scan -- a comparison run, a
 * public scan, a probe. The column holds null; the report needs a key.
 */
export const UNATTRIBUTED_SPEND = 'unattributed';

export type SpendReason = DepthReason | typeof UNATTRIBUTED_SPEND;

/* ── referrals ───────────────────────────────────────────────────── */

/** Where an invitation stands; mirrors the Prisma enum of the same name. */
export const ReferralStatus = {
  SIGNED_UP: 'SIGNED_UP',
  PAYING: 'PAYING',
  BLOCKED: 'BLOCKED',
} as const;

export type ReferralStatus = (typeof ReferralStatus)[keyof typeof ReferralStatus];

/** Where one reward stands; mirrors the Prisma enum of the same name. */
export const ReferralRewardStatus = {
  HOLDING: 'HOLDING',
  CREDITED: 'CREDITED',
  REVERSED: 'REVERSED',
} as const;

export type ReferralRewardStatus = (typeof ReferralRewardStatus)[keyof typeof ReferralRewardStatus];

// Product Knowledge Base vocabularies. Held as const objects (not TS enums) so
// the values stay plain string unions the app compares freely, while still
// giving enums-match-schema a runtime export to check against schema.prisma.
export const KbSection = {
  PRODUCT_INVENTORY: 'PRODUCT_INVENTORY',
  WORKFLOWS: 'WORKFLOWS',
  DETECTION_ENFORCEMENT: 'DETECTION_ENFORCEMENT',
  PLATFORM_COVERAGE: 'PLATFORM_COVERAGE',
  METRICS_CLAIMS: 'METRICS_CLAIMS',
  PRICING_BILLING: 'PRICING_BILLING',
  SUPPORT_OPERATIONS: 'SUPPORT_OPERATIONS',
  PRIVACY_SECURITY_LEGAL: 'PRIVACY_SECURITY_LEGAL',
  REPUTATION_PARTNERSHIPS: 'REPUTATION_PARTNERSHIPS',
} as const;
export type KbSection = (typeof KbSection)[keyof typeof KbSection];

export const KbCategory = {
  FEATURE: 'FEATURE',
  SCENARIO: 'SCENARIO',
  PLATFORM: 'PLATFORM',
  CAPABILITY: 'CAPABILITY',
  PLAN_LIMIT: 'PLAN_LIMIT',
  METRIC_CLAIM: 'METRIC_CLAIM',
  DATA: 'DATA',
  LIMITATION: 'LIMITATION',
} as const;
export type KbCategory = (typeof KbCategory)[keyof typeof KbCategory];

export const KbAudience = {
  PUBLIC: 'PUBLIC',
  INTERNAL: 'INTERNAL',
} as const;
export type KbAudience = (typeof KbAudience)[keyof typeof KbAudience];

export const KbStatus = {
  ACTIVE: 'ACTIVE',
  PLANNED: 'PLANNED',
  DEPRECATED: 'DEPRECATED',
} as const;
export type KbStatus = (typeof KbStatus)[keyof typeof KbStatus];

export const AuthEventKind = {
  PASSWORD_LOGIN: 'PASSWORD_LOGIN',
  GOOGLE_LOGIN: 'GOOGLE_LOGIN',
  MAGIC_LINK: 'MAGIC_LINK',
  SUPPORT_LINK: 'SUPPORT_LINK',
  REGISTER: 'REGISTER',
  PASSWORD_CHANGE: 'PASSWORD_CHANGE',
  PASSWORD_RESET: 'PASSWORD_RESET',
  EMAIL_VERIFY: 'EMAIL_VERIFY',
  LOGIN_FAILED: 'LOGIN_FAILED',
  HEARTBEAT: 'HEARTBEAT',
} as const;
export type AuthEventKind = (typeof AuthEventKind)[keyof typeof AuthEventKind];

export const AuthEventOutcome = {
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
} as const;
export type AuthEventOutcome = (typeof AuthEventOutcome)[keyof typeof AuthEventOutcome];

/** Which of the three impersonated fetchers made a crawl request. */
export const CrawlRequestSource = {
  SWEEP: 'sweep',
  SEARCH_PROBE: 'search_probe',
  PASSPORT: 'passport',
} as const;
export type CrawlRequestSource = (typeof CrawlRequestSource)[keyof typeof CrawlRequestSource];
